import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Loader from '../../components/Global/Loader';
import PersonalInfoDetails from '../../components/Student/Student Application/PersonalInfoDetails';
import PrimarySelection from '../../components/Student/Student Application/PrimarySelection';
import AreaOfInterest from '../../components/Student/Student Application/AreaOfInterest';
import { getDisplayValue } from '../../utils/displayValue';

function StudentApplicationForm() {

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [canApply, setCanApply] = useState(true);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    profileURL: "",
    title: "",
    fullName: "",
    fatherName: "",
    maritalStatus: "",
    gender: "",
    dateOfBirth: "",
    cnic: "",
    cellPhone: "",
    email: "",
    nationality: "",
    passportNumber: "",
    cityOfResidence: "",
  });

  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [majors, setMajors] = useState([]);

  const initialPrimarySelection = {
    country: 'UK',
    programme: ''
  };

  const initialSecondarySelection = {
    city: '',
    university: '',
    majors: []
  };

  const [primarySelection, setPrimarySelection] = useState(initialPrimarySelection);
  const [selectionForm, setSelectionForm] = useState(initialSecondarySelection);
  const [applications, setApplications] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isSelectionFormOpen, setIsSelectionFormOpen] = useState(true);
  const [selectionError, setSelectionError] = useState(null);
  const [previousPrimarySelection, setPreviousPrimarySelection] = useState(initialPrimarySelection);
  const [pendingProgrammeValue, setPendingProgrammeValue] = useState(null);
  const [showProgrammeChangeWarning, setShowProgrammeChangeWarning] = useState(false);
  const [pendingCountryValue, setPendingCountryValue] = useState(null);
  const [showCountryChangeWarning, setShowCountryChangeWarning] = useState(false);
  const [blockedUniversityPairs, setBlockedUniversityPairs] = useState({});

  useEffect(() => {
    getUserPersonalData();
  }, []);

  useEffect(() => {
    fetchFilterData();
  }, []); // Empty dependency array ensures it runs only once on mount

  useEffect(() => {
    setBlockedUniversityPairs(buildBlockedPairs(applications));
  }, [applications]);


  const getUserPersonalData = async () => {
    try {
      setIsLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/student`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // console.log(response.data.data);

      if (response && response.data?.data) {
        const {
          profileURL,
          title,
          firstName,
          lastName,
          fatherName,
          maritalStatus,
          nationality,
          passportNumber,
          gender,
          dateOfBirth,
          cnic,
          cellPhone,
          email,
          addressCity,
        } = response.data.data;

        const formattedDate = dateOfBirth ? dateOfBirth.split("T")[0] : "";
        const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

        const updatedPersonalInfo = {
          profileURL: profileURL || "",
          title: title || "",
          fullName: fullName || "",
          fatherName: fatherName || "",
          gender: gender || "",
          nationality: nationality || "",
          passportNumber: passportNumber || "",
          maritalStatus: maritalStatus || "",
          dateOfBirth: formattedDate,
          cnic: cnic || "",
          cellPhone: cellPhone || "",
          email: email || "",
          cityOfResidence: addressCity || "",
        };

        setFormData((prev) => ({
          ...prev,
          ...updatedPersonalInfo,
        }));

        const personalInfoRequiredFields = [
          "profileURL",
          "title",
          "fullName",
          "fatherName",
          "maritalStatus",
          "gender",
          "dateOfBirth",
          "cnic",
          "cellPhone",
          "email",
          "nationality",
          "cityOfResidence",
          "passportNumber",
        ];

        const allFieldsPresent = personalInfoRequiredFields.every((field) => {
          const value = updatedPersonalInfo[field];
          return typeof value === "string" ? value.trim() !== "" : Boolean(value);
        });

        setCanApply(allFieldsPresent);
      }
    } catch (error) {
      // console.error(error);
    } finally {
      setIsLoading(false);
    }
  };


  const validateForm = () => {
    const personalFields = [
      "profileURL",
      "title",
      "fullName",
      "fatherName",
      "maritalStatus",
      "gender",
      "dateOfBirth",
      "cnic",
      "cellPhone",
      "email",
      "nationality",
      "cityOfResidence",
      "passportNumber"
    ];

    const personalInfoValid = personalFields.every((field) => {
      const value = formData[field];
      return typeof value === "string" ? value.trim() !== "" : Boolean(value);
    });

    const hasSavedSelections = applications.length > 0;
    const selectionValid = hasSavedSelections;

    const isFormValid = personalInfoValid && selectionValid && canApply;

    setIsSubmitDisabled(!isFormValid);
  };

  const fetchFilterData = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/calculator/filter${queryString ? `?${queryString}` : ''}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // console.log('API Response:', response.data);

      if (!params.country) {
        // Initial load - set countries only
        setCountries(response.data.data.countries || []);
      } else if (!params.city) {
        // Program selected - set cities
        setCities(response.data.data.cities || []);
      } else if (!params.universityName) {
        // City selected - set universities
        setUniversities(response.data.data.universities || []); // Changed from universityName to universities
      } else {
        // University selected - set majors
        setMajors(response.data.data.majors || []);
      }

      return response.data?.data || {};
    } catch (error) {
      // console.error("Error fetching filter data:", error);
      throw error;
    }
  };

  const resetSelectionForm = (overrides = {}) => {
    setSelectionForm({
      ...initialSecondarySelection,
      ...overrides
    });
  };

  const buildBlockedPairs = (selections) => {
    return selections.reduce((acc, selection, index) => {
      const cityKey = getDisplayValue(selection.city, '');
      const universityKey = getDisplayValue(selection.university, '');

      if (!cityKey || !universityKey) {
        return acc;
      }

      const key = `${cityKey.toLowerCase()}::${universityKey.toLowerCase()}`;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(index);
      return acc;
    }, {});
  };

  const prefillSelectionOptions = async (selection) => {
    try {
      if (!selection.country) return;
      if (selection.programme) {
        await fetchFilterData({
          country: selection.country,
          program: selection.programme
        });
      }

      if (selection.city) {
        await fetchFilterData({
          country: selection.country,
          program: selection.programme,
          city: selection.city
        });
      }

      if (selection.university) {
        await fetchFilterData({
          country: selection.country,
          program: selection.programme,
          city: selection.city,
          universityName: getDisplayValue(selection.university, '')
        });
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to fetch saved university options. Please try again.");
    }
  };

  const handleMajorsChange = (selectedMajors) => {
    setSelectionForm((prev) => ({
      ...prev,
      majors: selectedMajors
    }));
    setSelectionError(null);
  };

  const handleSelectionFieldChange = async (field, value) => {
    setSelectionError(null);

    const updatedSelection = {
      ...selectionForm,
      [field]: value
    };

    if (field === 'city') {
      updatedSelection.university = '';
      updatedSelection.majors = [];
      setUniversities([]);
      setMajors([]);
    } else if (field === 'university') {
      updatedSelection.majors = [];
      setMajors([]);
    }

    setSelectionForm(updatedSelection);

    try {
      if (
        field === 'city' &&
        primarySelection.country &&
        primarySelection.programme &&
        value
      ) {
        await fetchFilterData({
          country: primarySelection.country,
          program: primarySelection.programme,
          city: value
        });
      } else if (
        field === 'university' &&
        primarySelection.country &&
        primarySelection.programme &&
        updatedSelection.city &&
        value
      ) {
        await fetchFilterData({
          country: primarySelection.country,
          program: primarySelection.programme,
          city: updatedSelection.city,
          universityName: getDisplayValue(value, '')
        });
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to load the latest university information. Please try again.");
    }
  };

  const handlePrimarySelectionChange = async (field, value) => {
    setSelectionError(null);

    if (field === 'country') {
      if (value === primarySelection.country) {
        return;
      }

      if (applications.length > 0) {
        setPreviousPrimarySelection(primarySelection);
        setPendingCountryValue(value);
        setShowCountryChangeWarning(true);
        return;
      }

      const updatedPrimary = {
        country: value || '',
        programme: ''
      };

      setPrimarySelection(updatedPrimary);
      setPreviousPrimarySelection(updatedPrimary);

      resetSelectionForm();
      setCities([]);
      setUniversities([]);
      setMajors([]);
      setApplications([]);
      setEditingIndex(null);
      setIsSelectionFormOpen(true);

      return;
    }

    if (field === 'programme') {
      if (value === primarySelection.programme) {
        return;
      }

      if (applications.length > 0) {
        setPreviousPrimarySelection(primarySelection);
        setPendingProgrammeValue(value);
        setShowProgrammeChangeWarning(true);
        return;
      }

      const updatedPrimary = {
        ...primarySelection,
        programme: value || ''
      };

      setPrimarySelection(updatedPrimary);
      setPreviousPrimarySelection(updatedPrimary);

      resetSelectionForm();
      setCities([]);
      setUniversities([]);
      setMajors([]);

      try {
        if (updatedPrimary.country && updatedPrimary.programme) {
          await fetchFilterData({
            country: updatedPrimary.country,
            program: updatedPrimary.programme
          });
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Unable to load the latest city information. Please try again.');
      }

      return;
    }
  };

  const confirmProgrammeChange = async () => {
    const updatedPrimary = {
      country: previousPrimarySelection.country,
      programme: pendingProgrammeValue || ''
    };

    setPrimarySelection(updatedPrimary);
    setPreviousPrimarySelection(updatedPrimary);
    setPendingProgrammeValue(null);
    setShowProgrammeChangeWarning(false);
    setSelectionError(null);

    setApplications([]);
    setEditingIndex(null);
    setIsSelectionFormOpen(true);
    resetSelectionForm();
    setCities([]);
    setUniversities([]);
    setMajors([]);

    try {
      if (updatedPrimary.country && updatedPrimary.programme) {
        await fetchFilterData({
          country: updatedPrimary.country,
          program: updatedPrimary.programme
        });
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to load the latest city information. Please try again.');
    }
  };

  const cancelProgrammeChange = () => {
    setPendingProgrammeValue(null);
    setShowProgrammeChangeWarning(false);
    setSelectionError(null);
    setPrimarySelection({ ...previousPrimarySelection });
  };

  const confirmCountryChange = () => {
    const updatedPrimary = {
      country: pendingCountryValue || '',
      programme: ''
    };

    setPrimarySelection(updatedPrimary);
    setPreviousPrimarySelection(updatedPrimary);
    setPendingCountryValue(null);
    setShowCountryChangeWarning(false);
    setSelectionError(null);

    setApplications([]);
    setEditingIndex(null);
    setIsSelectionFormOpen(true);
    resetSelectionForm();
    setCities([]);
    setUniversities([]);
    setMajors([]);
    setBlockedUniversityPairs({});
  };

  const cancelCountryChange = () => {
    setPendingCountryValue(null);
    setShowCountryChangeWarning(false);
    setSelectionError(null);
    setPrimarySelection({ ...previousPrimarySelection });
    setBlockedUniversityPairs(buildBlockedPairs(applications));
  };

  const saveSelection = () => {
    const trimmedSelection = {
      country: getDisplayValue(primarySelection.country, '').trim(),
      programme: getDisplayValue(primarySelection.programme, '').trim(),
      city: getDisplayValue(selectionForm.city, '').trim(),
      university: getDisplayValue(selectionForm.university, '').trim(),
      majors: Array.isArray(selectionForm.majors)
        ? selectionForm.majors.map((major) => getDisplayValue(major, '')).filter(Boolean)
        : []
    };

    if (!trimmedSelection.country || !trimmedSelection.programme) {
      setSelectionError('Please choose a study country and programme before saving a university.');
      return;
    }

    const selectionValid =
      trimmedSelection.city &&
      trimmedSelection.university &&
      trimmedSelection.majors.length > 0;

    if (!selectionValid) {
      setSelectionError('Please complete all fields before saving this university.');
      return;
    }

    const nextSelections =
      editingIndex !== null
        ? applications.map((item, idx) => (idx === editingIndex ? trimmedSelection : item))
        : [...applications, trimmedSelection];

    setSelectionError(null);
    setApplications(nextSelections);
    setEditingIndex(null);
    resetSelectionForm();
    setIsSelectionFormOpen(false);
  setBlockedUniversityPairs(buildBlockedPairs(nextSelections));
  };

  const handleEditSelection = async (index) => {
    const selection = applications[index];
    if (!selection) return;

    setEditingIndex(index);
    setSelectionError(null);
    setIsSelectionFormOpen(false);
    const updatedPrimary = {
      country: selection.country || primarySelection.country || initialPrimarySelection.country,
      programme: selection.programme || primarySelection.programme || ''
    };
    setPrimarySelection(updatedPrimary);
    setPreviousPrimarySelection(updatedPrimary);

    resetSelectionForm({
      city: selection.city || '',
      university: selection.university || '',
      majors: selection.majors || []
    });

    await prefillSelectionOptions(selection);
    setBlockedUniversityPairs(buildBlockedPairs(applications));
  };

  const handleRemoveSelection = (index) => {
  const updatedSelections = applications.filter((_, idx) => idx !== index);
  setApplications(updatedSelections);

    if (editingIndex === index) {
      setEditingIndex(null);
    }

    if (!updatedSelections.length) {
      resetSelectionForm();
      setIsSelectionFormOpen(true);
    }
    setBlockedUniversityPairs(buildBlockedPairs(updatedSelections));
  };

  const startNewSelection = () => {
    setEditingIndex(null);
    setSelectionError(null);
    resetSelectionForm();
    setIsSelectionFormOpen(true);
  };

  const cancelSelectionEditing = () => {
    setSelectionError(null);
    setEditingIndex(null);

    if (applications.length) {
      resetSelectionForm();
      setIsSelectionFormOpen(false);
    } else {
      resetSelectionForm();
      setIsSelectionFormOpen(true);
    }
    setBlockedUniversityPairs(buildBlockedPairs(applications));
  };

  const submitApplication = async () => {
    if (!applications.length) {
      setSelectionError('Please add at least one university before submitting.');
      return;
    }

    try {
      setIsLoading(true);

      const selectionsPayload = applications.map((selection) => ({
        country: getDisplayValue(selection.country, '').trim(),
        programme: getDisplayValue(selection.programme, '').trim(),
        city: getDisplayValue(selection.city, '').trim(),
        university: getDisplayValue(selection.university, '').trim(),
        majors: Array.isArray(selection.majors)
          ? [...new Set(selection.majors.map((major) => getDisplayValue(major, '')).filter(Boolean))]
          : []
      }));

      const payload = {
        ...formData,
        universitySelections: selectionsPayload
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/application/new`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 201) {
        setIsSubmitted(true);
      }
    } catch (error) {
      setIsLoading(false);
      setError(error.response?.data?.message || "Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // (Optional) Log the formData in a more readable way
    const dataToLog = { ...formData };
    Object.keys(dataToLog).forEach((key) => {
      if (dataToLog[key] instanceof File) {
        dataToLog[key] = `File: ${dataToLog[key].name}`;
      }
    });
    // console.log("Form Data Submitted:", dataToLog);

    // 4. Call submitApplication to actually send the data
    submitApplication();
  };
  useEffect(() => {
    validateForm();
  }, [formData, applications, selectionForm, canApply, primarySelection]);




  return (
    <div id="main-content" className="relative pl-36 pr-12 mt-16 w-full py-8 bg-[#f4f8fb]">
      {/* Loader */}
      {isLoading && <Loader />}
      {
        !canApply && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
              {/* Modal Content */}
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Incomplete Personal Information
              </h2>
              <h4 className="text-sm text-gray-600 mb-6">
                It appears you haven't filled out all of your personal details yet. Please complete all required fields before proceeding.
              </h4>
              <div className="flex justify-center space-x-4">
                {/* Cancel Button */}
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-500 focus:outline-none"
                >
                  Cancel
                </button>
                {/* Proceed Button */}
                <button
                  onClick={() => navigate("/profile")}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        isSubmitted && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
              {/* Modal Content */}
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Success!
              </h2>
              <h4 className="text-sm text-gray-600 mb-6">
                Your application has been submitted successfully.
              </h4>
              <div className="flex justify-end space-x-4">
                {/* Proceed Button */}
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        error && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
              {/* Modal Content */}
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Something went wrong!
              </h2>
              <h4 className="text-sm text-gray-600 mb-6">
                We encountered an issue while processing your request. Please try again later. If the problem persists, contact support.
              </h4>
              <div className="flex justify-end space-x-4">
                {/* Proceed Button */}
                <button
                  onClick={() => setError(null)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        showCountryChangeWarning && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Change study country?
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Changing the study country to <span className="font-semibold text-gray-800">{pendingCountryValue || 'no country selected'}</span> will clear all saved universities and reset your programme selection. Do you want to continue?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelCountryChange}
                  className="px-4 py-2 rounded border border-gray-400 text-gray-600 hover:bg-gray-100 transition"
                >
                  Keep current country
                </button>
                <button
                  type="button"
                  onClick={confirmCountryChange}
                  className="px-4 py-2 rounded bg-blue-500 text-white font-medium hover:bg-blue-600 transition"
                >
                  Change country
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        showProgrammeChangeWarning && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Change programme?
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Changing the programme to <span className="font-semibold text-gray-800">{pendingProgrammeValue || 'no programme selected'}</span> will clear all saved universities. Do you want to continue?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelProgrammeChange}
                  className="px-4 py-2 rounded border border-gray-400 text-gray-600 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmProgrammeChange}
                  className="px-4 py-2 rounded bg-blue-500 text-white font-medium hover:bg-blue-600 transition"
                >
                  Change
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Content Wrapper */}
      <div className="flex flex-wrap space-x-6">
        {/* Right Card */}
        <div className="px-10 py-6 bg-white rounded shadow-lg w-full">
          <PersonalInfoDetails formData={formData} />
          <hr className="border-t border-gray-300 mb-4" />
          <div className="mb-6">
            <PrimarySelection
              countries={countries}
              value={primarySelection}
              onChange={handlePrimarySelectionChange}
            />
            {applications.length > 0 && (
              <div className="mb-6">
                <h2 className="text-gray-800 text-lg font-semibold mb-4">Saved University Preferences</h2>
                <div className="space-y-4">
                  {applications.map((application, index) => {
                    const isEditingRow = editingIndex === index;

                    return (
                      <div
                        key={`${getDisplayValue(application.university)}-${index}`}
                        className={`border rounded-lg transition-colors duration-200 ${
                          isEditingRow
                            ? 'bg-white border-blue-300 shadow-sm'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        {!isEditingRow && (
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 transition-all duration-300 ease-in-out">
                            <div>
                              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                                Preference {index + 1}
                              </p>
                              <h4 className="text-lg font-semibold text-gray-800">
                                {getDisplayValue(application.university)}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {getDisplayValue(application.city, "")}, {getDisplayValue(application.country, "")} &bull; {getDisplayValue(application.programme, "")}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Majors: {getDisplayValue(application.majors)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditSelection(index)}
                                className="px-3 py-1.5 rounded border border-blue-500 text-blue-500 text-sm font-medium hover:bg-blue-500 hover:text-white transition"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemoveSelection(index)}
                                className="px-3 py-1.5 rounded border border-red-500 text-red-500 text-sm font-medium hover:bg-red-500 hover:text-white transition"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        )}

                        <div
                          className={`overflow-hidden transition-[max-height,opacity]  ease-out duration-500 ${
                            isEditingRow
                              ? 'max-h-[1200px] opacity-100'
                              : 'max-h-0 opacity-0'
                          }`}
                        >
                          {isEditingRow && (
                            <div className="p-4">
                              <div className="rounded-lg border border-blue-100 bg-white shadow-sm p-4">
                                <AreaOfInterest
                                  key={`edit-selection-${index}`}
                                  selection={selectionForm}
                                  onFieldChange={handleSelectionFieldChange}
                                  onMajorChange={handleMajorsChange}
                                  cities={cities}
                                  universities={universities}
                                  majors={majors}
                                  primarySelection={primarySelection}
                                  blockedPairs={blockedUniversityPairs}
                                  activeSelectionIndex={editingIndex}
                                />
                                {selectionError && (
                                  <p className="text-sm text-red-500 mt-2">{selectionError}</p>
                                )}
                                <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4">
                                  <button
                                    type="button"
                                    onClick={cancelSelectionEditing}
                                    className="px-4 py-2 rounded border border-gray-400 text-gray-600 hover:bg-gray-100 transition"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={saveSelection}
                                    className="px-4 py-2 rounded bg-blue-500 text-white font-medium hover:bg-blue-600 transition"
                                  >
                                    Update University
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isSelectionFormOpen ? (
              <>
                <AreaOfInterest
                  key={editingIndex === null ? 'new-selection-form' : 'inline-new-selection'}
                  selection={selectionForm}
                  onFieldChange={handleSelectionFieldChange}
                  onMajorChange={handleMajorsChange}
                  cities={cities}
                  universities={universities}
                  majors={majors}
                  primarySelection={primarySelection}
                  blockedPairs={blockedUniversityPairs}
                  activeSelectionIndex={editingIndex}
                />
                {selectionError && (
                  <p className="text-sm text-red-500 mt-2">{selectionError}</p>
                )}
                <div className="flex justify-end gap-3 mt-4">
                  {applications.length > 0 && (
                    <button
                      type="button"
                      onClick={cancelSelectionEditing}
                      className="px-4 py-2 rounded border border-gray-400 text-gray-600 hover:bg-gray-100 transition"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={saveSelection}
                    className="px-4 py-2 rounded bg-blue-500 text-white font-medium hover:bg-blue-600 transition"
                  >
                    {editingIndex !== null ? 'Update University' : 'Save University'}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={startNewSelection}
                  disabled={editingIndex !== null}
                  className={`px-4 py-2 rounded border transition ${
                    editingIndex !== null
                      ? 'border-blue-200 text-blue-200 cursor-not-allowed'
                      : 'border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white'
                  }`}
                >
                  Add Another University
                </button>
              </div>
            )}
          </div>
          {/* Submit Section */}
          <div className="flex justify-between mt-10 mr-5">
            <div>
              <button
                onClick={() => navigate("/dashboard")}
                className="bg-white text-gray-500 border border-gray-500 px-4 py-2 rounded hover:bg-gray-500 hover:text-white">
                Go Back to Dashboard
              </button>
            </div>

            <div>
              <button
                type="submit"
                onClick={handleSubmit}
                className={`px-4 py-2 rounded border ${isSubmitDisabled
                  ? "bg-white text-blue-200 border-blue-200 cursor-not-allowed"
                  : "bg-white text-blue-500 border-blue-500 hover:bg-blue-500 hover:text-white cursor-pointer"
                  }`}
                disabled={isSubmitDisabled}
              >
                Submit Application
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StudentApplicationForm
